import React from 'react';
import { cn } from '../../../lib/utils';
import { Badge } from '../../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Loader2, TrendingUp, TrendingDown, BookOpen } from 'lucide-react';
import { DirectionsDialogProps } from '../types';
import {
  StudyForm,
  StudyLanguage,
  DirectionYearlyQuota,
  UniversityDirection,
} from '../../../api/universities';

const FORM_OPTIONS: { value: StudyForm | 'all'; label: string }[] = [
  { value: 'all', label: 'All forms' },
  { value: 'kunduzgi', label: 'Kunduzgi' },
  { value: 'sirtqi', label: 'Sirtqi' },
  { value: 'kechki', label: 'Kechki' },
  { value: 'masofaviy', label: 'Masofaviy' },
];

const LANG_OPTIONS: { value: StudyLanguage | 'all'; label: string }[] = [
  { value: 'all', label: 'All languages' },
  { value: 'uz', label: "O'zbek" },
  { value: 'ru', label: 'Rus' },
  { value: 'kk', label: 'Qozoq' },
  { value: 'qq', label: 'Qoraqalpoq' },
  { value: 'ky', label: 'Qirgiz' },
  { value: 'tk', label: 'Turkman' },
  { value: 'tg', label: 'Tojik' },
  { value: 'en', label: 'English' },
];

interface FlatRow {
  direction: UniversityDirection;
  quota: DirectionYearlyQuota;
}

function flattenDirections(directions: UniversityDirection[]): FlatRow[] {
  const rows: FlatRow[] = [];
  for (const d of directions) {
    if (!d.quotas || d.quotas.length === 0) continue;
    for (const q of d.quotas) rows.push({ direction: d, quota: q });
  }
  return rows;
}

export function DirectionsDialog({
  isDark,
  isOpen,
  onOpenChange,
  university,
  directionsData,
  isLoading,
  form,
  language,
  onFormChange,
  onLanguageChange,
}: DirectionsDialogProps) {
  const flatRows = directionsData ? flattenDirections(directionsData.directions) : [];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'max-w-6xl max-h-[90vh] overflow-y-auto',
          isDark ? 'bg-[#1E293B] border-slate-700' : 'bg-white',
        )}
      >
        <DialogHeader>
          <DialogTitle
            className={cn(
              'flex items-center gap-3 flex-wrap',
              isDark ? 'text-white' : 'text-[#0F172A]',
            )}
          >
            {university?.name || 'Directions'}
            {directionsData?.year && (
              <Badge className="bg-[#0EA5A4]/10 text-[#0EA5A4] border-0 text-xs font-normal">
                {directionsData.year}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap pb-2">
          <Select
            value={form ?? 'all'}
            onValueChange={(v) => onFormChange(v === 'all' ? null : (v as StudyForm))}
          >
            <SelectTrigger
              className={cn(
                'w-44',
                isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white',
              )}
            >
              <SelectValue placeholder="Form" />
            </SelectTrigger>
            <SelectContent>
              {FORM_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={language ?? 'all'}
            onValueChange={(v) =>
              onLanguageChange(v === 'all' ? null : (v as StudyLanguage))
            }
          >
            <SelectTrigger
              className={cn(
                'w-44',
                isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white',
              )}
            >
              <SelectValue placeholder="Language" />
            </SelectTrigger>
            <SelectContent>
              {LANG_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#0EA5A4]" />
          </div>
        ) : directionsData ? (
          <div className="space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <StatBox
                isDark={isDark}
                Icon={BookOpen}
                color="#0EA5A4"
                value={`${directionsData.stats.total} / ${directionsData.stats.total_quotas}`}
                label="Directions / Quotas"
              />
              <StatBox
                isDark={isDark}
                Icon={TrendingUp}
                color="#10B981"
                value={String(directionsData.stats.highest_grant || 0)}
                label="Highest Grant"
              />
              <StatBox
                isDark={isDark}
                Icon={TrendingDown}
                color="#F59E0B"
                value={String(directionsData.stats.lowest_contract || 0)}
                label="Lowest Contract"
              />
            </div>

            {/* Table */}
            <div
              className="rounded-xl border overflow-hidden"
              style={{ borderColor: isDark ? '#334155' : '#e2e8f0' }}
            >
              <Table>
                <TableHeader>
                  <TableRow
                    className={cn(isDark ? 'border-slate-700' : 'border-slate-100')}
                  >
                    <TableHead
                      className={cn(
                        'font-semibold',
                        isDark ? 'text-slate-400' : 'text-slate-500',
                      )}
                    >
                      Code
                    </TableHead>
                    <TableHead
                      className={cn(
                        'font-semibold',
                        isDark ? 'text-slate-400' : 'text-slate-500',
                      )}
                    >
                      Direction
                    </TableHead>
                    <TableHead
                      className={cn(
                        'font-semibold hidden md:table-cell',
                        isDark ? 'text-slate-400' : 'text-slate-500',
                      )}
                    >
                      Form / Lang
                    </TableHead>
                    <TableHead
                      className={cn(
                        'font-semibold hidden lg:table-cell',
                        isDark ? 'text-slate-400' : 'text-slate-500',
                      )}
                    >
                      Subjects
                    </TableHead>
                    <TableHead
                      className={cn(
                        'font-semibold text-right',
                        isDark ? 'text-slate-400' : 'text-slate-500',
                      )}
                    >
                      Grant
                    </TableHead>
                    <TableHead
                      className={cn(
                        'font-semibold text-right',
                        isDark ? 'text-slate-400' : 'text-slate-500',
                      )}
                    >
                      Contract
                    </TableHead>
                    <TableHead
                      className={cn(
                        'font-semibold text-right hidden xl:table-cell',
                        isDark ? 'text-slate-400' : 'text-slate-500',
                      )}
                    >
                      Seats (G/C)
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {flatRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center py-8 text-slate-500"
                      >
                        No quotas found for these filters
                      </TableCell>
                    </TableRow>
                  ) : (
                    flatRows.map(({ direction, quota }) => (
                      <TableRow
                        key={quota.id}
                        className={cn(
                          'transition-colors',
                          isDark
                            ? 'border-slate-700 hover:bg-slate-800/50'
                            : 'border-slate-100 hover:bg-slate-50',
                        )}
                      >
                        <TableCell
                          className={cn(
                            'font-mono text-xs',
                            isDark ? 'text-slate-400' : 'text-slate-500',
                          )}
                        >
                          {direction.code}
                        </TableCell>
                        <TableCell
                          className={cn(
                            'font-medium text-sm',
                            isDark ? 'text-white' : 'text-[#0F172A]',
                          )}
                        >
                          {direction.name_uz}
                        </TableCell>
                        <TableCell
                          className={cn(
                            'hidden md:table-cell text-xs',
                            isDark ? 'text-slate-300' : 'text-slate-600',
                          )}
                        >
                          <div className="flex flex-col">
                            <span>{quota.form}</span>
                            <span className="text-slate-400">{quota.language}</span>
                          </div>
                        </TableCell>
                        <TableCell
                          className={cn(
                            'hidden lg:table-cell text-xs',
                            isDark ? 'text-slate-300' : 'text-slate-600',
                          )}
                        >
                          {(quota.subjects || []).map((s, i) => (
                            <div key={i}>
                              {s.name}{' '}
                              <span className="text-slate-400">({s.coefficient})</span>
                            </div>
                          ))}
                        </TableCell>
                        <TableCell className="text-right">
                          {quota.grant_score != null ? (
                            <Badge className="bg-emerald-500/10 text-emerald-500 border-0 font-mono">
                              {quota.grant_score}
                            </Badge>
                          ) : (
                            <span className="text-slate-400 text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {quota.contract_score != null ? (
                            <Badge className="bg-amber-500/10 text-amber-500 border-0 font-mono">
                              {quota.contract_score}
                            </Badge>
                          ) : (
                            <span className="text-slate-400 text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell
                          className={cn(
                            'text-right hidden xl:table-cell text-xs font-mono',
                            isDark ? 'text-slate-400' : 'text-slate-500',
                          )}
                        >
                          {quota.grant_count ?? '—'} / {quota.contract_count ?? '—'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function StatBox({
  isDark,
  Icon,
  color,
  value,
  label,
}: {
  isDark: boolean;
  Icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  color: string;
  value: string;
  label: string;
}) {
  return (
    <div
      className={cn(
        'p-3 rounded-xl text-center',
        isDark ? 'bg-slate-800' : 'bg-slate-50',
      )}
    >
      <Icon className="w-5 h-5 mx-auto mb-1" style={{ color }} />
      <p
        className={cn(
          'text-2xl font-bold',
          isDark ? 'text-white' : 'text-[#0F172A]',
        )}
      >
        {value}
      </p>
      <p className={cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>
        {label}
      </p>
    </div>
  );
}
